import { useEffect, useState } from 'preact/hooks';
import useVendor from './useVendor';
import { s3DataPrefix } from '../config/app';
import { redirectCampaignExpired } from '../helpers/utils';
import { BnclHookState } from '../types/hooks';
import { BnclCampaign } from '../types/voucher';

/**
 * Load BNCL campaign data based on campaignId
 * @return {{ data: Object, loading: boolean, error: boolean }}
 */
const useBnclCampaignData = (bnclCampaignId: string): BnclHookState<BnclCampaign> => {
    const [status, setStatus] = useState<BnclHookState<BnclCampaign>>({
        loading: true,
        error: false,
        data: null,
        isBncl: false,
    });

    const vendor = useVendor();

    useEffect(() => {
        (async () => {
            // Declaring these variables inside try{...} statement
            // Otherwise this leads to a babel compilation error when running Jest tests
            let response, campaignData;

            try {
                response = await fetch(`/${s3DataPrefix}/offers/bncl/${bnclCampaignId}/${vendor}.json`);
                if (!response.ok) {
                    throw new Error(`${response.status} ${response.statusText}`);
                }
                campaignData = await response.json();

                checkCampaignValidity(campaignData, vendor);

                setStatus({ loading: false, error: false, data: campaignData, isBncl: true });
            } catch (error: any) {
                console.error(`${error.message}`);
                redirectCampaignExpired(vendor);
                setStatus({ loading: false, error: true, data: null, isBncl: true });
            }
        })();
    }, [vendor, bnclCampaignId]);

    return status;
};

function checkCampaignValidity(campaignData, vendor) {
    // if this campaign not have offers data, redirect to the offer expired page
    if (!campaignData.vendorCampaign.offers.length) {
        throw new Error(`${vendor} does not have the correct data`);
    }

    const currentMoment = new Date().getTime();
    const campaignActivationMoment = new Date(campaignData.startDate).getTime();
    const campaignExpirationMoment = new Date(campaignData.endDate).getTime();

    if (campaignActivationMoment >= currentMoment || campaignExpirationMoment <= currentMoment) {
        if (process.env.APP_ENV !== 'dev') {
            throw new Error(`${vendor} campaign is not valid`);
        }
    }
}

export default useBnclCampaignData;
